import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import React, { useEffect } from 'react'
import {
  MatchedServiceLocationSelector,
  useMatchedServiceLocationsSelector,
} from '@user/components/Location'
import { useGetLocationDocUrl } from '@user/store/authorization/hooks'
import { useGetLocationDocs } from '@user/store/locationDocuments'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { ToTitleCase } from '@user/utils/ToTitleCase'
import LaunchIcon from '@mui/icons-material/Launch'
import Button from '@mui/material/Button'
import { LocationAttachmentsList } from './LocationAttachmentsList'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import AlertTitle from '@mui/material/AlertTitle'
import { useSelectedAttachmentStore } from '../selectedAttachmentStore'

type Props = {
  entityType: ENTITY_TYPES
  currentServiceLocation?: LocationDTO
}

export function LocationAttachments(props: Props) {
  const { entityType, currentServiceLocation } = props

  const { isSelectionEnable } = useSelectedAttachmentStore()

  const entityTypeLabel = ToTitleCase(entityType)

  const locationData = useMatchedServiceLocationsSelector()
  const { isMatched, selectedLocation } = locationData

  const externalLinkId =
    currentServiceLocation?.externalLinkId ||
    selectedLocation?.externalLinkId ||
    ''

  const documentUrl = useGetLocationDocUrl(externalLinkId ?? '')

  const { setLocationId, locationDocs = [], status } = useGetLocationDocs()

  useEffect(() => {
    setLocationId(externalLinkId)
  }, [selectedLocation, setLocationId])

  return (
    <Box>
      <Box>
        {isMatched ? (
          <Box mt={1.2}>
            <Box
              sx={{
                mb: isSelectionEnable ? 1.6 : 0.8,
                mx: 2,
              }}
            >
              {isSelectionEnable ? (
                <Typography sx={{ mb: 1.2 }} component={'div'} variant="body1">
                  Select files to attach to the agreement from the list below.
                </Typography>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  color="primary"
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'medium',
                  }}
                  href={documentUrl}
                  target="_blank"
                  data-test-id="view-documents-in-pestpac-button"
                >
                  View Documents in PestPac
                  <LaunchIcon
                    sx={{
                      ml: 1,
                      fontSize: '18px',
                    }}
                  />
                </Button>
              </Box>
            </Box>

            <Box px={2} mb={1.2} mt={1.4}>
              {currentServiceLocation?.id ? null : (
                <MatchedServiceLocationSelector
                  {...locationData}
                  label="Filter By Service Location"
                />
              )}
            </Box>

            {status == 'pending' ? (
              <Box display="flex" height={'14rem'}>
                <CircularProgress style={{ margin: 'auto' }} />
              </Box>
            ) : (
              <>
                {locationDocs?.length == 0 ? (
                  <Box px={2} mt={1.6}>
                    <Alert
                      severity="info"
                      icon={
                        <InfoOutlined
                          fontSize="small"
                          data-test-id="noLocationDocumentsInfoIcon"
                        />
                      }
                    >
                      <AlertTitle sx={{ mb: 0 }} color="#22548C">
                        No Location Documents found.
                      </AlertTitle>
                    </Alert>
                  </Box>
                ) : (
                  <LocationAttachmentsList dataList={locationDocs} />
                )}
              </>
            )}
          </Box>
        ) : (
          <Box px={2} mt={1.2}>
            <Alert
              severity="info"
              icon={
                <InfoOutlined
                  fontSize="small"
                  data-test-id="noAssociatedLocationInfoIcon"
                />
              }
            >
              <AlertTitle sx={{ mb: 0 }} color="#22548C">
                This {entityTypeLabel} has not been associated with a location.
              </AlertTitle>
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  )
}
